import humps from 'humps';
import { defineStore } from 'pinia';

import { isCancelError } from '@/apis/abort';
import * as CollaborationAPI from '@/apis/collaboration';
import communityApi from '@/apis/community';
import { getHttpErrorStatus } from '@/apis/http-errors';
import { LibraryAPI } from '@/apis/index';
import { useEnabledTypes } from '@/app/community/composables/useEnabledTypes';
import {
  blockConfirmationPopup,
  COMMUNITY_BLOCK_TOAST,
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  COMMUNITY_GENERIC_TYPES,
  COMMUNITY_UNBLOCK_TOAST,
  CONVERSATION_RESOURCE_ID_PREFIX,
  conversationMessageTypes,
} from '@/app/community/constants';
import { isInteractionMediaCommentType } from '@/app/community/utils/community-interactions';
import {
  getBrandHandleFromInteraction,
  userBlockedUpdatedEvent,
} from '@/app/community/utils/mixpanel-events';
import {
  applyReversibleCommunityInteractionUpdate,
  filterCommunityInteractions,
  getCountsQueryParams,
  getUnreadCountQueryParams,
  sortCommunityInteractions,
} from '@/app/community/utils/store-utils';
import { parseTimestamps } from '@/app/community/utils/time';
import { applyBulkUpdateToCollection } from '@/app/community/utils/websockets';
import { useCommunityInboxViewStore } from '@/stores/community-inbox-view';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useNotificationStore } from '@/stores/notification';
import { logger } from '@/utils/logger';
import { reverseUpdate } from '@/utils/optimism';

const notifyError = (message) => {
  const notificationStore = useNotificationStore();
  notificationStore.setToast({
    message,
    type: 'error',
  });
};

const emptyCountResponse = {
  ALL_MESSAGES: 0,
  INBOX: 0,
  ASSIGNED_TO_YOU: 0,
};

export const useCommunityInteractionStore = defineStore('communityInteraction', {
  resetOnBrandChange: true,
  state: () => ({
    communityInteractions: [],
    communityInteractionUnreadCounts: {},
    selectedCommunityInteractionId: null,
    finalCommunityInteractionLoaded: false,
    communityInteractionsNextPage: null,
    hasFetchedCommunityInteractions: false,
    selectedCommunityInteractionMedia: null,
    currentQuery: {},
    internalNotes: [],
    selectedInternalNoteId: null,
    finalInternalNoteLoaded: false,
    internalNoteNextPage: null,
    hasFetchedInternalNotes: false,
    pending: {
      communityInteraction: false,
      updateCommunityInteraction: false,
      communityInteractions: false,
      internalNotes: false,
    },
    communityInteractionsCancelToken: null,
    prevAssigneeId: null,
    interactionTypes: null,
    blockActionClicked: false,
    shouldAutoFocusReply: false,
  }),
  getters: {
    filteredCommunityInteractions: (state) =>
      filterCommunityInteractions(state.communityInteractions, state.currentQuery),
    selectedCommunityInteraction: (state) =>
      filterCommunityInteractions(state.communityInteractions, state.currentQuery).find(
        (communityInteraction) =>
          communityInteraction?.id === state.selectedCommunityInteractionId &&
          state.selectedCommunityInteractionId !== null,
      ),
    selectedCommunityInteractionTagIds() {
      if (!this.selectedCommunityInteraction?.tagIds?.length) {
        return [];
      }
      return this.selectedCommunityInteraction?.tagIds;
    },
    selectedInternalNote: (state) =>
      state.internalNotes.find((internalNote) => internalNote.id === state.selectedInternalNoteId),
    selectedInteractionGenericType() {
      const interactionType = this.selectedCommunityInteraction?.type;
      if (isInteractionMediaCommentType(this.selectedCommunityInteraction)) {
        return COMMUNITY_GENERIC_TYPES.COMMENT;
      }
      if (conversationMessageTypes().includes(interactionType)) {
        return COMMUNITY_GENERIC_TYPES.CONVERSATION;
      }

      return undefined;
    },
    hasTeamActivityFlag() {
      const flagStore = useFlagStore();
      return flagStore.ready && flagStore.flags.communityTeamActivity;
    },
  },
  actions: {
    updateInboxViewUnreadCount(countChange) {
      if (countChange === 0) return;

      const inboxViewStore = useCommunityInboxViewStore();
      if (inboxViewStore.activeView) {
        inboxViewStore.activeViewUnreadCount = Math.max(
          0,
          inboxViewStore.activeViewUnreadCount + countChange,
        );
      }
    },
    calculateInboxViewCountChange(beforeState, afterState) {
      // Calculate how the inbox view count should change based on state transitions
      const { isRead: beforeIsRead, isArchived: beforeIsArchived } = beforeState;
      const { isRead: afterIsRead, isArchived: afterIsArchived } = afterState;

      if (this.hasTeamActivityFlag) {
        // Total-count semantics: only archive transitions move messages in/out of the active view
        if (beforeIsArchived === afterIsArchived) return 0;
        return afterIsArchived ? -1 : 1;
      }

      // Legacy unread-count semantics: only unread, non-archived interactions count
      let countChange = 0;

      // Handle isRead changes (only for non-archived interactions)
      if (beforeIsRead !== afterIsRead && !afterIsArchived) {
        countChange += afterIsRead ? -1 : 1;
      }

      // Handle isArchived changes (only for unread interactions)
      if (beforeIsArchived !== afterIsArchived && !afterIsRead) {
        countChange += afterIsArchived ? -1 : 1;
      }

      return countChange;
    },
    // TODO: TEAM_ACTIVITY - Remove once `community-team-activity` flag is fully rolled out (read/unread sunset).
    reverseInboxViewCountForPayload(interactions, payload) {
      // Reverse inbox view count changes based on payload
      // interactions can be a single interaction object or an array
      if (payload?.isRead == null && payload?.isArchived == null) return;

      const items = Array.isArray(interactions) ? interactions : [interactions];
      let viewCountChange = 0;

      items.forEach(({ isRead, isArchived }) => {
        const optimisticState = {
          isRead: payload.isRead ?? isRead,
          isArchived: payload.isArchived ?? isArchived,
        };
        const originalState = { isRead, isArchived };
        // Calculate reverse: from optimistic state back to original
        viewCountChange += this.calculateInboxViewCountChange(optimisticState, originalState);
      });

      this.updateInboxViewUnreadCount(viewCountChange);
    },
    reverseBulkUpdateCounts(preUpdateInteractions, payload) {
      if (this.hasTeamActivityFlag) {
        this.applyBulkTotalCountChanges(preUpdateInteractions, payload, true);
        return;
      }
      // Reverse optimistic count updates when bulk update fails
      const identityStore = useIdentityStore();
      const currentUserId = identityStore.identity.id;
      let allMessagesCountChange = 0;
      let assignedToMeCountChange = 0;
      let inboxCountChange = 0;

      // Reverse the count changes by calculating the opposite
      if (Object.prototype.hasOwnProperty.call(payload, 'isRead')) {
        preUpdateInteractions.forEach(({ assigneeId, isRead }) => {
          if (payload.isRead && !isRead) {
            // We decremented, now increment back
            inboxCountChange += 1;
            allMessagesCountChange += 1;
            if (assigneeId === currentUserId) assignedToMeCountChange += 1;
          } else if (!payload.isRead && isRead) {
            // We incremented, now decrement back
            inboxCountChange -= 1;
            allMessagesCountChange -= 1;
            if (assigneeId === currentUserId) assignedToMeCountChange -= 1;
          }
        });
      }

      if (Object.prototype.hasOwnProperty.call(payload, 'isArchived')) {
        preUpdateInteractions.forEach(({ assigneeId, isRead }) => {
          const reverseChange = payload.isArchived ? 1 : -1;

          if (!isRead) {
            inboxCountChange += reverseChange;
            if (assigneeId === currentUserId) {
              assignedToMeCountChange += reverseChange;
            }
          }
        });
      }

      this.updateInteractionCounts(
        allMessagesCountChange,
        assignedToMeCountChange,
        inboxCountChange,
      );

      // Reverse inbox view count
      this.reverseInboxViewCountForPayload(preUpdateInteractions, payload);
    },
    async handleUserBlocked({ interaction, afterUpdateHook = null, toBlocked = true } = {}) {
      const res = await this.updateCommunityInteraction(
        interaction.id,
        {
          isAuthorBlocked: toBlocked,
        },
        false,
        null,
        afterUpdateHook,
      );
      if (res.data.data) {
        const [channel, type] = interaction.type.split('_');
        userBlockedUpdatedEvent(
          toBlocked,
          channel,
          type,
          interaction.brandId,
          getBrandHandleFromInteraction(interaction),
        );
      }
    },
    async toggleBlockState(interaction, afterUpdateHook = null, toBlocked = true) {
      const { title, confirmMessage, confirmAlias, cancelAlias } =
        blockConfirmationPopup(toBlocked);
      const notificationStore = useNotificationStore();
      const confirmed = await notificationStore.confirm(title, confirmMessage, {
        confirmAlias,
        cancelAlias,
      });

      if (confirmed) {
        this.blockActionClicked = true;
        const toast = toBlocked ? COMMUNITY_BLOCK_TOAST : COMMUNITY_UNBLOCK_TOAST;
        try {
          await this.handleUserBlocked({ interaction, afterUpdateHook, toBlocked });
          notificationStore.setToast({
            message: toast.SUCCESS,
            type: 'success',
          });
        } catch (error) {
          if (getHttpErrorStatus(error) === 409) {
            notifyError(toast.ERROR.RECENTLY_BLOCKED);
          } else {
            notifyError(toast.ERROR.DEFAULT);
          }
        }
        this.blockActionClicked = false;
      }
    },
    setMessagesLoadingState(loading = true, showInternalNotes = false) {
      const state = showInternalNotes ? 'internalNotes' : 'communityInteractions';
      this.pending[state] = loading;
    },
    async getCommunityInteraction({ brandIds, id, types }) {
      this.pending.communityInteraction = true;
      try {
        const res = await communityApi.getCommunityInteractions({ brandIds, id, types });
        this.pending.communityInteraction = false;
        return res.data.data.map(parseTimestamps)[0];
      } catch {
        this.pending.communityInteraction = false;
        return {};
      }
    },
    async fetchCommunityInteractions(params, signal) {
      const res = await communityApi.getCommunityInteractions(params, { signal });
      const data = res.data.data.map(parseTimestamps);
      return {
        data,
        nextPage: res.data.paging.next,
        count: res.data.paging.count,
      };
    },
    async getCommunityInteractions(params, skipFilter = false) {
      this.pending.communityInteractions = true;
      const { next } = params;
      if (!next) {
        this.communityInteractions = [];
        this.communityInteractionsNextPage = null;

        // Update currentQuery with the isArchived filter from params
        // If isArchived is explicitly passed (even as undefined), update it
        // This ensures stale values don't persist when navigating between routes
        if ('isArchived' in params) {
          this.currentQuery = {
            ...this.currentQuery,
            isArchived: params.isArchived,
          };
        }
      }

      this.communityInteractionsCancelToken?.abort();
      this.communityInteractionsCancelToken = new AbortController();
      const signal = this.communityInteractionsCancelToken.signal;
      try {
        const { data, nextPage } = await this.fetchCommunityInteractions(params, signal);
        const newCommunityInteractionIds = data.map(
          (communityInteraction) => communityInteraction.id,
        );

        this.communityInteractionsNextPage = nextPage;
        this.finalCommunityInteractionLoaded =
          data.length === 0 || !this.communityInteractionsNextPage;

        this.communityInteractions = skipFilter
          ? [...this.communityInteractions, ...data]
          : filterCommunityInteractions(
              sortCommunityInteractions(
                [
                  ...this.communityInteractions.filter(
                    (communityInteraction) =>
                      !newCommunityInteractionIds.includes(communityInteraction.id),
                  ),
                  ...data,
                ],
                this.currentQuery,
              ),
              this.currentQuery,
              true,
            );
      } catch (e) {
        if (isCancelError(e)) {
          return;
        }
      }
      this.pending.communityInteractions = false;
    },
    async updateCommunityInteraction(
      id,
      payload,
      flagChange = false,
      interaction = null,
      afterUpdateHook = null,
      throwError = false,
    ) {
      const communityInteraction =
        interaction || this.communityInteractions.find((ci) => ci.id === id);
      if (!communityInteraction) return {};
      const { assigneeId, isRead, isArchived } = communityInteraction;
      const identityStore = useIdentityStore();
      const currentUserId = identityStore.identity?.id;
      this.calculateCountChanges(payload, isRead, isArchived, assigneeId, currentUserId);
      if (flagChange) {
        this.pending.updateCommunityInteraction = true;
      }

      // Optimistically update inbox view unread count
      if (payload?.isRead != null || payload?.isArchived != null) {
        const beforeState = { isRead, isArchived };
        const afterState = {
          isRead: payload.isRead ?? isRead,
          isArchived: payload.isArchived ?? isArchived,
        };
        const countChange = this.calculateInboxViewCountChange(beforeState, afterState);
        this.updateInboxViewUnreadCount(countChange);
      }

      // Add non-standard fields for optimistic update
      const optimisticPayload = { ...payload };
      if ('isSpamOverride' in payload) {
        optimisticPayload.isSpam = payload.isSpamOverride;
        delete optimisticPayload.isSpamOverride;
      }

      // Interaction may not be in the current list
      // e.g. archiving an open message from ConversationDetail that isn't visible in the archive list
      // Only apply optimistic update if it exists in the list.
      const target = this.communityInteractions.find((ci) => ci.id === id);
      if (target) {
        applyReversibleCommunityInteractionUpdate(
          this.communityInteractions,
          id,
          humps.camelizeKeys(optimisticPayload),
        );
      }
      if (afterUpdateHook) afterUpdateHook({ commentId: id, payload });

      try {
        const res = await communityApi.updateCommunityInteraction(id, payload);
        const communityInteractionData = parseTimestamps(res.data.data);

        if (target) {
          this.communityInteractions = this.communityInteractions.map((ci) => {
            if (ci.id === id) {
              communityInteractionData.searchResultSnippet = ci.searchResultSnippet;
              return communityInteractionData;
            }
            return ci;
          });
        } else {
          // Add interaction that was not in the preview list before.
          // It should appear in the correct filtered view.
          this.communityInteractions.push(communityInteractionData);
        }

        if (flagChange) {
          this.pending.updateCommunityInteraction = false;
        }
        return res;
      } catch (err) {
        logger.error(
          `Failed to update community interaction (id: ${id}, brand:${communityInteraction.brandId})`,
          {},
          err,
        );

        if (target) {
          reverseUpdate(this.communityInteractions, id);
        }
        if (afterUpdateHook) afterUpdateHook({ commentId: id, payload, undo: true });
        this.calculateCountChanges(payload, isRead, isArchived, assigneeId, currentUserId, true);
        // Reverse inbox view unread count update on error
        // Note: reverseUpdate has already restored interaction to original state
        this.reverseInboxViewCountForPayload({ isRead, isArchived }, payload);
        if (flagChange) {
          this.pending.updateCommunityInteraction = false;
        }
        // bubble up errors related to blocking users to handle specific errors
        if (payload.isAuthorBlocked !== undefined || throwError) {
          throw err;
        }
      }

      return {};
    },
    async bulkUpdateCommunityInteractions(payload) {
      const { ids } = payload;
      const preUpdateInteractions = this.communityInteractions.filter((interaction) =>
        ids.includes(interaction.id),
      );

      // Optimistically update counts before API call
      const shouldUpdateCounts =
        Object.prototype.hasOwnProperty.call(payload, 'isRead') ||
        Object.prototype.hasOwnProperty.call(payload, 'isArchived') ||
        (this.hasTeamActivityFlag && Object.prototype.hasOwnProperty.call(payload, 'assigneeId'));

      if (shouldUpdateCounts) {
        this.calculateBulkCountChanges(preUpdateInteractions, payload);
      }

      try {
        const res = await communityApi.bulkUpdateCommunityInteractions(payload);
        return res;
      } catch (err) {
        // Reverse the optimistic count updates on error
        if (shouldUpdateCounts) {
          this.reverseBulkUpdateCounts(preUpdateInteractions, payload);
        }
        throw err;
      }
    },
    applyBulkUpdateResponse(interactions) {
      // use the response from the bulk update to update the state of community interactions
      interactions.forEach((interaction) => {
        const index = this.communityInteractions.findIndex((item) => item.id === interaction.id);
        if (index >= 0) {
          this.communityInteractions.splice(index, 1, parseTimestamps(interaction));
        }
      });
    },
    // TODO: TEAM_ACTIVITY - Remove once `community-team-activity` flag is fully rolled out (read/unread sunset).
    async setCommunityInteractionRead(interaction) {
      if (this.hasTeamActivityFlag) return;
      const target = interaction ?? this.selectedCommunityInteraction;
      if (target && !target.isOwned && !target.isRead) {
        await this.updateCommunityInteraction(target.id, { isRead: true });
      }
    },
    // TODO: TEAM_ACTIVITY - Remove once `community-team-activity` flag is fully rolled out (read/unread sunset).
    async getUnreadInteractionCountsV2() {
      if (this.hasTeamActivityFlag) return;
      // skip get unread count if no enabledTypes
      const { enabledTypes } = useEnabledTypes();
      if (!enabledTypes.value?.length) {
        this.communityInteractionUnreadCounts = emptyCountResponse;
        return;
      }
      const { signal } = this.refreshCountsCancelToken();
      const payload = this.buildCountsPayload(getUnreadCountQueryParams);

      let counts = emptyCountResponse;
      try {
        const res = await communityApi.getUnreadCommunityInteractionCountsV2(payload, { signal });
        // the response from the backend is not consistent with the front end, ASSIGNED_TO_YOU not the same as ASSIGNED_TO_ME
        counts = {
          ALL_MESSAGES: res.data.ALL_MESSAGES,
          ASSIGNED_TO_YOU: res.data.ASSIGNED_TO_ME ?? res.data.ASSIGNED_TO_YOU,
          INBOX: res.data.INBOX,
        };
      } catch (e) {
        if (isCancelError(e)) {
          return;
        }
      }
      this.communityInteractionUnreadCounts = counts;
    },
    async getInteractionCounts() {
      const { enabledTypes } = useEnabledTypes();
      if (!enabledTypes.value?.length) {
        this.communityInteractionUnreadCounts = emptyCountResponse;
        return;
      }
      const { signal } = this.refreshCountsCancelToken();
      const payload = this.buildCountsPayload(getCountsQueryParams);

      let counts = emptyCountResponse;
      try {
        const res = await communityApi.getCommunityInteractionCounts(payload, { signal });
        counts = {
          ALL_MESSAGES: res.data.ALL_MESSAGES,
          ASSIGNED_TO_YOU: res.data.ASSIGNED_TO_ME ?? res.data.ASSIGNED_TO_YOU,
          INBOX: res.data.INBOX,
        };
      } catch (e) {
        if (isCancelError(e)) return;
      }
      this.communityInteractionUnreadCounts = counts;
    },
    refreshCountsCancelToken() {
      this.unreadInteractionCountsCancelToken?.abort();
      this.unreadInteractionCountsCancelToken = new AbortController();
      return this.unreadInteractionCountsCancelToken;
    },
    buildCountsPayload(toParams) {
      // We don't need the isArchived and isRead query param for the counts endpoints
      const { isArchived, isRead, ...query } = this.currentQuery;
      if (query.brandId) {
        // TODO: This can be removed once multi brand inbox is fully rolled out
        query.brandIds = `${query.brandId}`;
        delete query.brandId;
      }
      return toParams(query);
    },
    setSelectedCommunityInteraction(communityInteraction, shouldAutoFocus = false) {
      this.selectedCommunityInteractionId = communityInteraction?.id || null;
      this.shouldAutoFocusReply = shouldAutoFocus;
    },
    async clearCommunityInteractions() {
      this.selectedCommunityInteractionId = null;
      this.communityInteractions = [];
      this.finalCommunityInteractionLoaded = false;
      this.communityInteractionsNextPage = null;
    },
    async setCommunityInteractionMedia({ brandId, mediaId }, axiosConfig = {}) {
      const res = await LibraryAPI.getInteractionMediaById(
        {
          brandId,
          mediaId,
        },
        axiosConfig,
        { useCache: true },
      );
      this.selectedCommunityInteractionMedia = res.data;
    },
    clearCommunityInteractionMedia() {
      this.selectedCommunityInteractionMedia = null;
    },
    updateSelectedInteraction({ payload: value, undo = false }) {
      if (undo) {
        Object.keys(
          Object.fromEntries(
            Object.entries(value).filter((item) => typeof item?.[1] === 'boolean'),
          ),
        ).forEach((key) => {
          value[key] = !value[key];
        });
      }
      const interaction = this.communityInteractions.find(
        (communityInteraction) =>
          communityInteraction.id === this.selectedCommunityInteractionId &&
          this.selectedCommunityInteractionId !== null,
      );
      if (interaction) {
        this.communityInteractions = this.communityInteractions.map((item) =>
          item.id !== interaction.id ? item : { ...interaction, ...value },
        );
      }
    },
    setCommentDisabled(payload) {
      return new Promise((resolve, reject) => {
        communityApi
          .updatePostEnabled(payload)
          .then(() => {
            this.selectedCommunityInteractionMedia.sourceData.commentDisabled =
              payload.commentDisabled;
            resolve();
          })
          .catch(() => reject());
      });
    },
    setCurrentQuery(newQuery) {
      this.currentQuery = newQuery;
    },
    bulkUpdatePlatformConversations({ payload }) {
      // If interaction rule ids filter is applied when the websocket receives an event, we need
      // to see if the incoming interaction exists in the current list of interactions. We do not
      // have any other way of telling if the interaction is valid or not, so we collect the IDs
      // to exclude here
      const interactionRuleIdFilter = [];
      const interactionRuleIds = this.currentQuery?.interactionRuleIds?.split(',') || [];

      const currentIds = this.communityInteractions.map((item) => item.id);

      // Track read and archived state changes for inbox view count
      const beforeStates = new Map();
      this.communityInteractions.forEach((interaction) => {
        beforeStates.set(interaction.id, {
          isRead: interaction.isRead,
          isArchived: interaction.isArchived,
        });
      });

      payload.forEach((update) => {
        if (interactionRuleIds.length > 0) {
          if (!currentIds.includes(update.data?.id)) {
            interactionRuleIdFilter.push(update.data?.id);
          }
        }
        applyBulkUpdateToCollection(this.communityInteractions, 'id', update);
      });

      // Calculate inbox view count changes based on isRead and isArchived state changes
      let viewCountChange = 0;
      this.communityInteractions.forEach((interaction) => {
        const beforeState = beforeStates.get(interaction.id);
        if (!beforeState) return;

        const afterState = {
          isRead: interaction.isRead,
          isArchived: interaction.isArchived,
        };
        viewCountChange += this.calculateInboxViewCountChange(beforeState, afterState);
      });
      this.updateInboxViewUnreadCount(viewCountChange);

      this.communityInteractions = filterCommunityInteractions(
        sortCommunityInteractions(this.communityInteractions, this.currentQuery),
        this.currentQuery,
        false,
        interactionRuleIdFilter,
      );
    },
    removeCommunityInteractionsFromStore({ interactionIds }) {
      const currentId = this.selectedCommunityInteractionId;
      const currentIndex = this.communityInteractions.map((item) => item.id).indexOf(currentId);

      // if selected interaction is removed, select next interaction
      if (interactionIds.includes(currentId)) {
        const interactions = this.communityInteractions;
        const notRemoved = (interaction) => !interactionIds.includes(interaction.id);

        // find first interaction that is not removed
        const nextInteraction =
          interactions.slice(currentIndex + 1).find(notRemoved) ||
          interactions.slice(0, currentIndex).reverse().find(notRemoved);
        this.selectedCommunityInteractionId = nextInteraction?.id || null;
      }
      this.communityInteractions = this.communityInteractions.filter(
        (interaction) => !interactionIds.includes(interaction.id),
      );

      if (this.hasTeamActivityFlag) {
        this.getInteractionCounts();
      } else {
        this.getUnreadInteractionCountsV2();
      }
    },
    optimisticallyAssignTag(tagId) {
      applyReversibleCommunityInteractionUpdate(
        this.communityInteractions,
        this.selectedCommunityInteractionId,
        {
          tagIds: { add: [tagId] },
        },
      );
    },
    async assignTagToSelectedInteraction({ brandId, tag: assignedTag, isNew }) {
      let tag = { ...assignedTag };
      if (isNew) {
        const tagStore = useCommunityMessageTagsStore();
        // Create a temporary tag, and assign it to the interaction
        tag = await tagStore.createTag({
          brandId,
          tag,
          optimismCallback: this.optimisticallyAssignTag,
        });
        // Remove temporary tag from interaction
        reverseUpdate(this.communityInteractions, this.selectedCommunityInteractionId);
      }

      try {
        // Send request to backend to assign the real tag to the interaction.
        await this.updateCommunityInteraction(this.selectedCommunityInteractionId, {
          tagIds: { add: [tag.id] },
        });
      } catch (e) {
        logger.error(
          `Error: ${e} - assigning tag: ${tag?.id} from interaction: ${this.selectedCommunityInteractionId}`,
        );
        notifyError('There was an error assigning your tag');
        throw e;
      }
    },
    async removeTagFromSelectedInteraction(tag, notify = true) {
      try {
        await this.updateCommunityInteraction(
          this.selectedCommunityInteractionId,
          { tagIds: { remove: [tag.id] } },
          false,
          null,
          null,
          true,
        );
      } catch (e) {
        logger.error(
          `Error ${e} removing tag: ${tag?.id} from interaction: ${this.selectedCommunityInteractionId}`,
        );
        if (notify) {
          notifyError('There was an error removing your tag');
        }
        throw e;
      }
    },
    formatInternalNotePreview(internalNote, users) {
      // users may be undefined if the API response has no referencedObjects
      const authorUser = users?.[internalNote.userId];
      const rawResourceId = internalNote.resourceId;
      const resourceId =
        typeof rawResourceId === 'string' &&
        rawResourceId.startsWith(CONVERSATION_RESOURCE_ID_PREFIX)
          ? rawResourceId
          : Number(rawResourceId);
      const noteItem = {
        id: internalNote.id,
        brandId: internalNote.brandId,
        resourceType: COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE,
        resourceId,
        type: `${internalNote.meta?.channel}_${internalNote.meta?.messageType}`,
        text: internalNote.text,
        createdAt: internalNote.createdAt,
        isRead: internalNote.meta?.isRead,
        isAdComment: internalNote.meta?.isAdComment || false,
        messageInteractionId: internalNote.meta?.messageInteractionId,
        authorUser,
        users,
      };
      return noteItem;
    },
    async getInternalNotesPreview({
      brandIds,
      commentId,
      mentionedUserIds,
      meta,
      createdBefore,
      createdAfter,
      limit,
      offset,
      sort,
      next,
    }) {
      this.internalNotesCancelToken?.abort();
      this.internalNotesCancelToken = new AbortController();
      const signal = this.internalNotesCancelToken.signal;
      this.pending.internalNotes = true;
      try {
        const payload = next
          ? { next }
          : {
              brandIds,
              commentId,
              resourceTypes: COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE,
              mentionedUserIds,
              createdBefore,
              createdAfter,
              meta,
              limit,
              offset,
              sort,
            };
        const response = await CollaborationAPI.getBrandComments(payload, { signal });

        const { data, paging, referencedObjects } = response.data;
        const notes = [];

        data.forEach((item) => {
          if (item.meta?.channel && item.meta?.messageType) {
            notes.push(this.formatInternalNotePreview(item, referencedObjects?.user));
          }
        });
        this.internalNotes = [...this.internalNotes, ...notes];
        this.internalNoteNextPage = notes.length !== 0 ? paging.next : null;
        this.finalInternalNoteLoaded = notes.length === 0 || !this.internalNoteNextPage;
      } catch (e) {
        if (isCancelError(e)) {
          return;
        }
        notifyError('Failed to load internal notes. Please try again.');
      } finally {
        this.pending.internalNotes = false;
      }
    },
    setSelectedInternalNote(internalNote) {
      this.selectedInternalNoteId = internalNote?.id ?? null;
    },
    clearInternalNotes() {
      if (this.communityInteractions.length) {
        this.clearCommunityInteractions();
      }
      this.selectedInternalNoteId = null;
      this.internalNotes = [];
      this.finalInternalNoteLoaded = false;
      this.internalNoteNextPage = null;
      this.selectedCommunityInteractionId = null;
      this.communityInteractions = [];
    },
    calculateAssigneeCountChanges(
      newAssigneeId,
      oldAssigneeId,
      communityInteraction,
      reverse = false,
    ) {
      const identityStore = useIdentityStore();
      const currentUserId = identityStore.identity.id;
      const allMessagesChange = 0;
      let assignedToMeChange = 0;
      const inboxChange = 0;

      // Total-count semantics: archived messages never count toward ASSIGNED_TO_YOU,
      // and assignment between two non-current users is a no-op.
      const gate = this.hasTeamActivityFlag
        ? !communityInteraction.isArchived
        : !communityInteraction.isRead;

      if (gate) {
        if (newAssigneeId === currentUserId) assignedToMeChange += 1;
        else if (oldAssigneeId === currentUserId) assignedToMeChange -= 1;
      }

      if (reverse) {
        assignedToMeChange *= -1;
      }

      return { allMessagesChange, assignedToMeChange, inboxChange };
    },
    calculateCountChanges(payload, isRead, isArchived, assigneeId, currentUserId, reverse = false) {
      if (this.hasTeamActivityFlag) {
        const reverseSign = reverse ? -1 : 1;
        let inboxChange = 0;
        let assignedChange = 0;

        if (payload.isArchived !== undefined && payload.isArchived !== isArchived) {
          const direction = (payload.isArchived ? -1 : 1) * reverseSign;
          inboxChange += direction;
          if (assigneeId === currentUserId) assignedChange += direction;
        }

        // Assignment changes only affect ASSIGNED_TO_YOU, and only for non-archived messages
        const afterArchived = payload.isArchived ?? isArchived;
        if (
          payload.assigneeId !== undefined &&
          payload.assigneeId !== assigneeId &&
          !afterArchived
        ) {
          if (payload.assigneeId === currentUserId) assignedChange += reverseSign;
          else if (assigneeId === currentUserId) assignedChange -= reverseSign;
        }

        if (inboxChange === 0 && assignedChange === 0) return;
        // ALL_MESSAGES tracks INBOX in lockstep: there's no UI surface for an "all + archived" total today.
        // Active-view count is handled by the caller via calculateInboxViewCountChange / reverseInboxViewCountForPayload.
        this.updateInteractionCounts(inboxChange, assignedChange, inboxChange);
        return;
      }
      let allMessagesCountChange = 0;
      let assignedToMeCountChange = 0;
      let inboxCountChange = 0;

      // reverse will only be true when we need to undo an optimistic update
      // either add or subtract a count change in the event we need to undo an optimistic update
      const countOperation = (change) => (reverse ? -change : change);

      if (payload.isRead !== undefined) {
        if (payload.isRead) {
          if (isArchived) {
            // only the all message inbox shows archived posts as well
            allMessagesCountChange += countOperation(-1);
          } else {
            inboxCountChange += countOperation(-1);
            allMessagesCountChange += countOperation(-1);
          }
          if (assigneeId === currentUserId) assignedToMeCountChange += countOperation(-1);
        } else {
          if (isArchived) {
            allMessagesCountChange += countOperation(1);
          } else {
            inboxCountChange += countOperation(1);
            allMessagesCountChange += countOperation(1);
          }
          if (assigneeId === currentUserId) assignedToMeCountChange += countOperation(1);
        }
      }

      if (payload.isArchived !== undefined) {
        if (payload.isArchived) {
          if (!isRead) {
            inboxCountChange += countOperation(-1);

            if (assigneeId === currentUserId) {
              assignedToMeCountChange += countOperation(-1);
            }
          }
        } else if (!isRead) {
          inboxCountChange += countOperation(1);

          if (assigneeId === currentUserId) {
            assignedToMeCountChange += countOperation(1);
          }
        }
      }
      this.updateInteractionCounts(
        allMessagesCountChange,
        assignedToMeCountChange,
        inboxCountChange,
      );
    },
    applyBulkTotalCountChanges(communityInteractions, payload, reverse = false) {
      if (payload.isArchived === undefined && payload.assigneeId === undefined) return;
      const identityStore = useIdentityStore();
      const currentUserId = identityStore.identity.id;
      const reverseSign = reverse ? -1 : 1;
      let assignedChange = 0;
      let inboxChange = 0;
      communityInteractions.forEach(({ assigneeId, isArchived }) => {
        if (payload.isArchived !== undefined && isArchived !== payload.isArchived) {
          const direction = (payload.isArchived ? -1 : 1) * reverseSign;
          inboxChange += direction;
          if (assigneeId === currentUserId) assignedChange += direction;
        }
        const afterArchived = payload.isArchived ?? isArchived;
        if (
          payload.assigneeId !== undefined &&
          payload.assigneeId !== assigneeId &&
          !afterArchived
        ) {
          if (payload.assigneeId === currentUserId) assignedChange += reverseSign;
          else if (assigneeId === currentUserId) assignedChange -= reverseSign;
        }
      });
      if (inboxChange === 0 && assignedChange === 0) return;
      // ALL_MESSAGES tracks INBOX in lockstep: there's no UI surface for an "all + archived" total today.
      this.updateInteractionCounts(inboxChange, assignedChange, inboxChange);
      this.updateInboxViewUnreadCount(inboxChange);
    },
    calculateBulkCountChanges(communityInteractions, payload) {
      if (this.hasTeamActivityFlag) {
        this.applyBulkTotalCountChanges(communityInteractions, payload);
        return;
      }
      const identityStore = useIdentityStore();
      const currentUserId = identityStore.identity.id;
      let allMessagesCountChange = 0;
      let assignedToMeCountChange = 0;
      let inboxCountChange = 0;

      if (payload.isRead !== undefined) {
        communityInteractions.forEach(({ assigneeId, isRead }) => {
          if (payload.isRead) {
            // only update counts if the interaction is unread
            if (!isRead) {
              inboxCountChange -= 1;
              allMessagesCountChange -= 1;
              if (assigneeId === currentUserId) assignedToMeCountChange -= 1;
            }
          } else {
            inboxCountChange += 1;
            allMessagesCountChange += 1;
            if (assigneeId === currentUserId) assignedToMeCountChange += 1;
          }
        });
      }

      if (payload.isArchived !== undefined) {
        communityInteractions.forEach(({ assigneeId, isRead }) => {
          const change = payload.isArchived ? -1 : 1;

          if (!isRead) {
            // only update counts if interaction is unread
            inboxCountChange += change;
            if (assigneeId === currentUserId) {
              assignedToMeCountChange += change;
            }
          }
        });
      }
      this.updateInteractionCounts(
        allMessagesCountChange,
        assignedToMeCountChange,
        inboxCountChange,
      );

      // Update inbox view unread count for bulk updates
      if (payload.isRead !== undefined || payload.isArchived !== undefined) {
        let viewCountChange = 0;
        communityInteractions.forEach(({ isRead, isArchived }) => {
          const beforeState = { isRead, isArchived };
          const afterState = {
            isRead: payload.isRead ?? isRead,
            isArchived: payload.isArchived ?? isArchived,
          };
          viewCountChange += this.calculateInboxViewCountChange(beforeState, afterState);
        });
        this.updateInboxViewUnreadCount(viewCountChange);
      }
    },
    updateInteractionCounts(allMessagesChange, assignedToMeChange, inboxCountChange) {
      this.communityInteractionUnreadCounts = {
        ALL_MESSAGES: Math.max(
          0,
          (this.communityInteractionUnreadCounts?.ALL_MESSAGES ?? 0) + allMessagesChange,
        ),
        ASSIGNED_TO_YOU: Math.max(
          0,
          (this.communityInteractionUnreadCounts?.ASSIGNED_TO_YOU ?? 0) + assignedToMeChange,
        ),
        INBOX: Math.max(0, (this.communityInteractionUnreadCounts?.INBOX ?? 0) + inboxCountChange),
      };
    },

    executeRuleActionOnInteraction(interactionToUpdate) {
      const storeInteractions = this.communityInteractions;
      const indexToUpdate = storeInteractions.findIndex(
        (storeInteraction) => storeInteraction.id === interactionToUpdate.id,
      );

      if (indexToUpdate === -1) {
        return;
      }

      const updatedInteraction = {
        ...storeInteractions[indexToUpdate],
        isArchived: interactionToUpdate.isArchived,
        tagIds: interactionToUpdate.tagIds,
      };

      this.communityInteractions = [
        ...storeInteractions.slice(0, indexToUpdate),
        updatedInteraction,
        ...storeInteractions.slice(indexToUpdate + 1),
      ];

      if (
        storeInteractions[indexToUpdate].isArchived === false &&
        interactionToUpdate.isArchived === true
      ) {
        // calculate inbox unread count changes
        const identityStore = useIdentityStore();
        const currentUserId = identityStore.identity?.id;
        const { assigneeId, isRead, isArchived } = storeInteractions[indexToUpdate];
        this.calculateCountChanges(
          { isArchived: true },
          isRead,
          isArchived,
          assigneeId,
          currentUserId,
        );

        // calculate custom view unread count changes
        this.updateInboxViewUnreadCount(-1);
      }
    },
  },
});
